import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// A composited transform, never an animated width, so the entrance never triggers layout.
const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export const Section = styled.section`
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-top: 0px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-top: 0px;
  }
`;

export const TableCard = styled.div`
  overflow: hidden;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
`;

export const HeaderRow = styled.tr`
  background: var(--color-tab-background);
`;

export const HeaderCell = styled.th<{ $align?: "left" | "right" }>`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  text-align: ${({ $align }) => $align ?? "left"};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-light);
  white-space: nowrap;
`;

export const BodyRow = styled.tr`
  cursor: pointer;
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border-light);
  }

  &:hover {
    background: var(--color-tab-background);
  }
`;

export const Cell = styled.td<{ $align?: "left" | "right" }>`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  text-align: ${({ $align }) => $align ?? "left"};
  vertical-align: middle;
  font-size: var(--text-base);
  color: var(--color-text-default);
  white-space: nowrap;
`;

export const NameText = styled.div`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  margin-top: ${SPACING.xs}px;
`;

export const Dot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

export const TypeLabel = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const RateText = styled.span`
  color: var(--color-text-secondary);
`;

export const MutedText = styled.span`
  color: var(--color-text-secondary);
`;

export const ValueText = styled.span`
  font-weight: var(--font-weight-bold);
`;

export const EndsText = styled.span`
  color: var(--color-text-secondary);
`;

/* PROGRESS column: a slim payoff bar with the percent beside it. */

export const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 120px;
`;

export const ProgressPercent = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
`;

export const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: var(--color-background-3);
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $width: number }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: var(--color-primary);
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SummaryFooter = styled.div`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const CardList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Card = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  cursor: pointer;
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:hover {
    background: var(--color-tab-background);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const RateChip = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
  background: var(--color-background-3);
`;

export const CardValue = styled.span`
  font-size: var(--text-xxxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const CardProgressHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardPaidOff = styled.span`
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
`;

export const CardAmounts = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-align: right;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const MobileSummary = styled.p`
  margin: ${SPACING.md}px 0 0;
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
  text-align: center;
`;
